import {
    Alert,
    Box,
    Card,
    CircularProgress,
    Grid,
    TablePagination,
    Typography,
    useTheme,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { FormEvent, useEffect, useState } from "react";
import { ReadingImageCard } from "./ReadingImageCard";
import { ReadingImageFullscreen } from "./ReadingImageFullscreen";
import {
    initialClearAll,
    initialIntermediateFilter,
    ReadingFilterForm,
} from "./ReadingFilterForm";
import { readings } from "./Reading";

const ReadingImages = () => {
    const theme = useTheme();
    const [rowsCount, setRowsCount] = useState(0);
    const [pagination, setPagination] = useState({ page: 0, limit: 100 });
    const [intermediateFilter, setIntermediateFilter] = useState<any>(
        initialIntermediateFilter
    );
    const [filter, setFilter] = useState<any>({});
    const [billingMonth, setBillingMonth] = useState("");
    const [clearAll, setClearAll] = useState(initialClearAll);
    const [fullscreenIndex, setFullscreenIndex] = useState<number | null>(null);

    const { data, isFetching: isLoading } = useQuery(
        ["reading-images", pagination.page, pagination.limit, filter],
        () => readings({ pagination, filter }),
        {
            enabled: Object.values(filter).filter((x) => Boolean(x)).length > 0,
            onSuccess(res) {
                if (res.data) {
                    setRowsCount(res.data.count);
                }
            },
        }
    );

    const rows = data?.data?.rows ?? [];

    const handleClearAll = () => {
        setFilter({});
        setIntermediateFilter(initialIntermediateFilter);
        setBillingMonth("");
        setClearAll({
            district: true,
            village: true,
            hamlet: true,
            powerstation: true,
            property: true,
            connectionType: true,
            operator: true,
        });
        setPagination({ page: 0, limit: 100 });
        setTimeout(() => {
            setClearAll(initialClearAll);
        }, 0);
    };

    const handleSubmit = (ev: FormEvent<HTMLFormElement>) => {
        ev.preventDefault();
        setPagination((p) => ({ ...p, page: 0 }));
        const [year, month] = intermediateFilter.billingMonth?.split("-") ?? [
            "",
            "",
        ];
        setFilter({
            ...intermediateFilter,
            ...(intermediateFilter.billingMonth
                ? { month, year, billingMonth: undefined }
                : {}),
        });
    };

    useEffect(() => {
        if (billingMonth) {
            const [year, month] = billingMonth.split("-") ?? ["", ""];
            setIntermediateFilter((prev: any) => ({
                ...prev,
                year,
                month: parseInt(month),
            }));
        }
    }, [billingMonth]);

    useEffect(() => {
        setIntermediateFilter((prev: any) => ({ ...prev, meterStatus: "active" }));
    }, []);

    return (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Card
                elevation={0}
                sx={{
                    bgcolor: theme.palette.common.white,
                    p: 2,
                    mb: "1rem",
                }}
            >
                <Typography variant="h5" color="GrayText" mb={1.5}>
                    Reading Images
                </Typography>

                <ReadingFilterForm
                    intermediateFilter={intermediateFilter}
                    setIntermediateFilter={setIntermediateFilter}
                    billingMonth={billingMonth}
                    setBillingMonth={setBillingMonth}
                    clearAll={clearAll}
                    handleClearAll={handleClearAll}
                    handleSubmit={handleSubmit}
                    isLoading={isLoading}
                />
            </Card>

            {!Boolean(
                Object.values(filter).filter((x) => Boolean(x)).length
            ) && <Alert severity="info">Please select a filter.</Alert>}

            {filter &&
                Object.values(filter).some(Boolean) &&
                rows.length === 0 &&
                !isLoading && (
                    <Alert severity="info">Found 0 reading images.</Alert>
                )}

            {isLoading && (
                <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                </Box>
            )}

            {!isLoading && rows.length > 0 && (
                <>
                    <Grid container spacing={2}>
                        {rows.map((row: any, index: number) => (
                            <Grid
                                item
                                xs={12}
                                sm={6}
                                md={4}
                                lg={3}
                                key={row.id}
                            >
                                <ReadingImageCard
                                    reading={row}
                                    onView={() => setFullscreenIndex(index)}
                                />
                            </Grid>
                        ))}
                    </Grid>

                    <TablePagination
                        component="div"
                        count={rowsCount}
                        page={pagination.page}
                        onPageChange={(_, newPage) =>
                            setPagination({ ...pagination, page: newPage })
                        }
                        rowsPerPage={pagination.limit}
                        onRowsPerPageChange={(e) =>
                            setPagination({
                                page: 0,
                                limit: parseInt(e.target.value, 10),
                            })
                        }
                        rowsPerPageOptions={[100, 250, 500]}
                    />
                </>
            )}

            <ReadingImageFullscreen
                open={fullscreenIndex !== null}
                rows={rows}
                activeIndex={fullscreenIndex}
                onClose={() => setFullscreenIndex(null)}
                onIndexChange={setFullscreenIndex}
            />
        </Box>
    );
};

export default ReadingImages;
